<template>
  <div class="table_container theme-transition">
    <!-- Enhanced Header -->
    <div class="table_header card-base theme-transition">
      <div class="header-content">
        <!-- Turnover Display -->
        <div class="turnover-card">
          <div class="turnover-icon">
            <font-awesome-icon icon="chart-line" />
          </div>
          <div class="turnover-details">
            <span class="turnover-label">Expected Turnover</span>
            <h2 class="turnover-amount">{{ $utils.formatMoney(total + tipsTotal) }}</h2>
          </div>
        </div>

        <!-- Search and Filters -->
        <div class="search-filters">
          <div class="search-box">
            <font-awesome-icon icon="search" class="search-icon" />
            <input 
              type="text" 
              v-model="tableName" 
              placeholder="Search tables or sale #..."
              class="search-input"
            >
          </div>
          <div class="filter-box" v-if="users.length > 0">
            <v-select 
              v-model="createdUser" 
              :options="users"
              placeholder="Filter by user"
              class="user-select"
            >
              <template slot="no-options">
                <b>Type to search ...</b>
              </template>
            </v-select>
          </div>
          <div class="filter-box tables-list-limit-box">
            <label for="tables-list-limit" class="limit-label">Max tables</label>
            <div class="limit-controls">
              <input
                id="tables-list-limit"
                type="number"
                class="limit-input"
                v-model.number="listLimitDraft"
                min="1"
                max="100000"
                step="10"
                title="Maximum number of open tables to load (newest first). Default 10000. Min 1. All line items for each loaded table are included."
                @keyup.enter="applyListLimit"
              >
              <button type="button" class="limit-btn" @click="adjustListLimit(-10)" title="Decrease by 10">−</button>
              <button type="button" class="limit-btn" @click="adjustListLimit(10)" title="Increase by 10">+</button>
              <button type="button" class="limit-apply-btn" @click="applyListLimit">Apply</button>
            </div>
            <span class="limit-hint">{{ (tables && tables.length) || 0 }} tables · max {{ listLimitApplied }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="table-tabs card-base theme-transition">
      <div class="table-tabs-left">
        <button
          class="table-tab-btn"
          :class="{ active: activeTableTab === 'offline' }"
          @click="activeTableTab = 'offline'"
        >
          Offline tables (sync pending)
        </button>
        <button
          class="table-tab-btn"
          :class="{ active: activeTableTab === 'online' }"
          @click="activeTableTab = 'online'"
        >
          Online tables
        </button>
      </div>
      <div class="table-tabs-right">
        <button
          v-if="activeTableTab === 'offline'"
          class="offline-sync-btn"
          :disabled="!isOnline || isSyncingOffline || offlineTables.length === 0"
          @click="syncOfflineTables"
        >
          <font-awesome-icon icon="sync-alt" :spin="isSyncingOffline" />
          <span>{{ isSyncingOffline ? 'Syncing...' : 'Sync Offline Tables' }}</span>
        </button>
      </div>
    </div>

    <!-- Table Grid -->
    <div ref="tablesScrollContainer" class="tables-grid-container" @scroll="updateScrollButtons">
      <div class="tables-grid">
        <div
          v-for="tab in displayedTables"
          :key="getTableKey(tab)"
          class="table-item-wrap"
        >
          <SingleTable @click="openTable(tab)" :tab="tab" />
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-if="displayedTables.length === 0" class="empty-state">
        <font-awesome-icon icon="inbox" class="empty-icon" />
        <h3>No {{ activeTableTab === 'offline' ? 'Offline' : 'Online' }} Tables Found</h3>
        <p>{{ activeTableTab === 'offline' ? 'No tables are waiting to be synced' : 'Start a new sale or adjust your filters' }}</p>
      </div>

    <div class="scroll-controls">
        <button class="scroll-btn" @click="scrollTables('up')" :disabled="!canScrollUp">
          <font-awesome-icon icon="chevron-up" />
        </button>
        <button class="scroll-btn" @click="scrollTables('down')" :disabled="!canScrollDown">
          <font-awesome-icon icon="chevron-down" />
        </button>
      </div>
    </div>

    <!-- Floating Action Button -->
    <div class="fab-container" :class="{ 'fab-open': fabOpen }">
      <!-- Backdrop -->
      <div class="fab-backdrop" v-if="fabOpen" @click="fabOpen = false"></div>
      
      <!-- Speed Dial Actions -->
      <transition-group name="fab-item" tag="div" class="fab-actions">
        <div v-if="fabOpen" key="new-sale" class="fab-action-item" @click="goToPos">
          <span class="fab-label">{{ currentSaleLines }}</span>
          <div class="fab-action-button">
            <font-awesome-icon icon="plus" />
          </div>
        </div>

        <div v-if="fabOpen" key="create-table" class="fab-action-item" @click="goToCreateTable">
          <span class="fab-label">Create Table</span>
          <div class="fab-action-button">
            <font-awesome-icon icon="plus" />
          </div>
        </div>
        
        <div
          v-if="fabOpen && isOnline"
          key="merge"
          class="fab-action-item"
          @click="mergeTable"
        >
          <span class="fab-label">Merge Tables</span>
          <div class="fab-action-button">
            <font-awesome-icon icon="code-merge" />
          </div>
        </div>

        <div v-if="fabOpen && !isAndroidMode" key="drawer" class="fab-action-item" @click="openCashDrawer">
          <span class="fab-label">Open Cash Drawer</span>
          <div class="fab-action-button">
            <font-awesome-icon icon="cash-register" />
          </div>
        </div>
        
        <div v-if="fabOpen && isOnline" key="payout" class="fab-action-item">
          <span class="fab-label">Payout</span>
          <div class="fab-action-button" @click="showPayoutModal">
            <font-awesome-icon icon="money-bill-wave" />
          </div>
        </div>

        <div v-if="fabOpen && isOnline" key="deposit" class="fab-action-item">
          <span class="fab-label">Deposit to Safe</span>
          <div class="fab-action-button" @click="showDepositModal">
            <font-awesome-icon icon="vault" />
          </div>
        </div>

        <div v-if="fabOpen && isOnline" key="cashup" class="fab-action-item" @click="userCashup">
          <span class="fab-label">End User Shift</span>
          <div class="fab-action-button">
            <font-awesome-icon icon="coins" />
          </div>
        </div>
      </transition-group>
      
      <!-- Main FAB -->
      <button class="fab-main" @click="fabOpen = !fabOpen" :class="{ 'fab-active': fabOpen }">
        <font-awesome-icon :icon="fabOpen ? 'times' : 'bars'" class="fab-icon" />
      </button>
    </div>

    <!-- Modals -->
    <OpenTableSheet ref="tableSheet" @updateAction="getOpenTables" :tab="setTable" />
    <CapturePayout ref="payoutModal" />
    <CashDrop ref="depositModal" />
  </div>
</template>

<script>
import SingleTable from "@/components/point-of-sale/table/SingleTable";
import OpenTableSheet from "@/components/point-of-sale/table/OpenTableSheet";
import { Storage } from "@/services/Storage";
import CapturePayout from "@/components/point-of-sale/Accounts/CapturePayout";
import CashDrop from "@/components/point-of-sale/Accounts/CashDrop";
import Swal from 'sweetalert2';
import connectivityMonitor from "@/services/connectivityMonitor";
import paidTablesOutbox from "@/services/paidTablesOutbox";
import tableServerClient from "@/services/tableServerClient";

export default {
  name: "AllTables",
  components: { CashDrop, CapturePayout, OpenTableSheet, SingleTable },
  computed: {
    nameSearch() {
      return this.tables.filter(table => {
        const name = (table.Name || '').toLowerCase();
        const total = (table.total || 0).toString().toLowerCase();
        const customer = (table.customer || '').toLowerCase();
        const searchTerm = this.tableName.toLowerCase();
        const saleNum = (table.saleNumber || table.SaleNum || '').toString().toLowerCase();
        
        return name.includes(searchTerm) || 
               total.includes(searchTerm) || 
               customer.includes(searchTerm) ||
               saleNum.includes(searchTerm);
      });
    },
    filterTables() {
      return this.nameSearch.filter(table => {
        let createdUser = this.createdUser || '';
        const tableUser = (table.user || '').toLowerCase();
        return tableUser.includes(createdUser.toLowerCase());
      })
    },
    offlineTables() {
      return this.filterTables.filter(this.isPendingSyncTable);
    },
    onlineTables() {
      return this.filterTables.filter(table => !this.isPendingSyncTable(table));
    },
    displayedTables() {
      return this.activeTableTab === 'offline' ? this.offlineTables : this.onlineTables;
    },
    users() {
      let usersToSearch = [];
      for (let i = 0; i < this.tables.length; i++) {
        let user = this.tables[i].user;
        if (!usersToSearch.includes(user)) {
          usersToSearch.push(user);
        }
      }
      return usersToSearch;
    },
    total() {
      let tables = this.tables;
      if (!tables) return 0.00;
      let total = 0;
      for (let i in tables) {
        let tab = tables[i];
        let tabTotal = 0;
        let items = tab.items;
        for (let j = 0; j < items.length; j++) {
          tabTotal += items[j].Line;
          let instructions = items[j].instructions;
          for (let k = 0; k < instructions.length; k++) {
            let price = instructions[k].Price;
            total += price;
          }
        }
        this.tables[i].total = parseFloat(tabTotal.toFixed(2));
        total += tabTotal;
      }
      return total;
    },
    settings() {
      return this.$store.getters.getDeviceSettings;
    },
    currentSaleLines() {
      return this.$store.getters.getSaleLines.length > 0 ? `Return to Sale` : `New Sale`
    },
    isAndroidMode() {
      return window.androidMode;
    }
  },
  created() {
    this.isOnline = connectivityMonitor.getStatus();
    this._unsubOnline = connectivityMonitor.subscribe('online', () => this.isOnline = true);
    this._unsubOffline = connectivityMonitor.subscribe('offline', () => this.isOnline = false);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollButtons);
    this._unsubOnline && this._unsubOnline();
    this._unsubOffline && this._unsubOffline();
    if (this._tablesPoller) {
      clearInterval(this._tablesPoller);
      this._tablesPoller = null;
    }
  },
  async beforeMount() {
    this.getOpenTables()
    await this.initTablesPolling();
  },
  activated() {
    // Refresh tables when returning to this view (e.g., after payment/void)
    // Silent: no full-screen loader flash on return.
    console.log('[AllTables] View activated, refreshing tables');
    this.getOpenTables(undefined, { silent: true });
  },
  mounted() {
    window.addEventListener('resize', this.updateScrollButtons);
    this.$nextTick(this.updateScrollButtons);
  },
  data() {
    return {
      tableName: '',
      createdUser: ``,
      tipsTotal: 0,
      setTable: {},
      tables: [],
      fabOpen: false,
      isOnline: true,
      isMainPC: false,
      _unsubOnline: null,
      _unsubOffline: null,
      _tablesPoller: null,
      _isLoadingTables: false,
      activeTableTab: 'online',
      isSyncingOffline: false,
      canScrollUp: false,
      canScrollDown: false,
      hasOverflow: false,
      scrollStep: 280,
      listLimitDraft: 10000,
      listLimitApplied: 10000,
      /**
       * Local debounce for FAB New Sale double-click.
       * Complements store isStartingNewSale (beginNewSaleSession).
       * Pattern mirrors processingGRV from Core App GRV fix.
       */
      processingNewSale: false,
    }
  },
  watch: {
    displayedTables() {
      this.$nextTick(this.updateScrollButtons);
    },
    activeTableTab() {
      this.$nextTick(this.updateScrollButtons);
    }
  },
  methods: {
    adjustListLimit(delta) {
      const next = Number(this.listLimitDraft || 10000) + delta;
      this.listLimitDraft = Math.min(100000, Math.max(1, next));
    },
    applyListLimit() {
      const limit = Math.min(100000, Math.max(1, parseInt(this.listLimitDraft, 10) || 10000));
      this.listLimitDraft = limit;
      this.listLimitApplied = limit;
      return this.getOpenTables(limit);
    },
    isPendingSyncTable(table) {
      if (!table) return false;

      const synced = table.synced;
      if (synced === false || synced === 0 || synced === '0' || synced === 'false') return true;
      if (table.pendingSync === true || table.isOffline === true || table.offline === true) return true;

      const name = String(table.Name || '');
      if (name.endsWith('_offline')) return true;

      return false;
    },
    getTableKey(table) {
      return String(table?.saleNumber || table?.SaleNum || table?.Name || '');
    },
    updateScrollButtons() {
      const container = this.$refs.tablesScrollContainer;
      if (!container) {
        this.canScrollUp = false;
        this.canScrollDown = false;
        this.hasOverflow = false;
        return;
      }

      const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
      this.hasOverflow = container.scrollHeight > container.clientHeight + 2;
      this.canScrollUp = container.scrollTop > 2;
      this.canScrollDown = container.scrollTop < (maxScrollTop - 2);
    },
    scrollTables(direction) {
      const container = this.$refs.tablesScrollContainer;
      if (!container) return;
      const amount = direction === 'up' ? -this.scrollStep : this.scrollStep;
      container.scrollBy({ top: amount, behavior: 'smooth' });
    },
    goToCreateTable() {
      this.fabOpen = false;
      return this.$router.push('/create-table');
    },
    async syncOfflineTables() {
      if (this.isSyncingOffline) return;
      if (!this.isOnline) {
        await Swal.fire({
          icon: 'warning',
          title: 'Offline',
          text: 'Connect to the internet before syncing offline tables.'
        });
        return;
      }

      this.isSyncingOffline = true;
      this.$utils.showLoading();
      try {
        const store = await Storage.getDefaultStore();
        const settings = await Storage.getDeviceSettings();
        const pendingTables = (Array.isArray(this.tables) ? this.tables : []).filter(this.isPendingSyncTable);

        let synced = 0;
        let failed = 0;

        for (const table of pendingTables) {
          const payload = {
            ...table,
            SaleNum: table?.SaleNum || table?.saleNumber || 0,
            Name: String(table?.Name || '').replace(/_offline$/, ''),
            User: table?.User || table?.user || '',
            Customer: table?.Customer || table?.customer || '',
            PC: table?.PC || table?.pc || settings?.PC || '',
            server: table?.server || settings?.server || '',
            synced: false,
            isOffline: true
          };

          try {
            await tableServerClient.addOrUpdateTable(payload, store.StoreDB);
            synced += 1;
          } catch (error) {
            failed += 1;
            console.error('[AllTables] Failed to sync table', payload?.SaleNum, error);
          }
        }

        await this.getOpenTables();
        await Swal.fire({
          icon: failed > 0 ? 'warning' : 'success',
          title: failed > 0 ? 'Sync completed with issues' : 'Sync complete',
          text: pendingTables.length > 0
            ? `Synced: ${synced}${failed > 0 ? `, Failed: ${failed}` : ''}`
            : 'No offline tables were found to sync.'
        });
      } catch (error) {
        await Swal.fire({
          icon: 'error',
          title: 'Sync failed',
          text: error?.message || 'Unable to sync offline tables right now.'
        });
      } finally {
        this.isSyncingOffline = false;
        this.$utils.hideLoading();
      }
    },
    /**
     * Starts a 30-second polling timer that calls `getOpenTables` to keep the
     * local table list in sync with changes (especially voids) made on other
     * terminals. Runs on ALL PCs (Main + non-main) because there is no
     * real-time broadcast channel for table events - polling is the only way
     * non-main terminals learn about voids initiated by other terminals.
     *
     * Side effect: sets `this.isMainPC` for downstream logic that still
     * depends on knowing whether this device is the Main PC (e.g. table
     * creation in `goToPos`).
     *
     * The interval is cleared in `deactivated()` and `beforeDestroy`.
     */
    async initTablesPolling() {
      try {
        // Match Table Server Configuration / tableServerClient (Android = host flag only).
        this.isMainPC = await tableServerClient.checkIsMainPC();
      } catch (e) {
        this.isMainPC = false;
      }

      // Guard: only avoid creating a duplicate interval. Polling now runs on
      // ALL PCs so non-main terminals can sync voids from other terminals.
      if (this._tablesPoller) return;

      this._tablesPoller = setInterval(() => {
        // Refresh the local table list to pick up voids (and other changes)
        // made on other terminals. On non-main PCs this is cheap: it reads
        // from the local table-server cache, not the backend.
        // Silent refresh: no full-screen loader during background sync.
        this.getOpenTables(undefined, { silent: true });
      }, 30000);
    },
    getOpenTables(limit, options = {}) {
      const silent = options.silent === true;
      if (this._isLoadingTables) return;
      this._isLoadingTables = true;
      if (!silent) this.$utils.showLoading()
      if (this.listLimitDraft == null || this.listLimitApplied == null) {
        const stored = this.$store.getters.getTablesListLimit || 10000;
        this.listLimitDraft = stored;
        this.listLimitApplied = stored;
      }
      const payload = limit != null ? { limit } : undefined;
      return this.$store.dispatch('getAllTables', payload)
        .then(async (res) => {
          this.listLimitApplied = this.$store.getters.getTablesListLimit || this.listLimitApplied;
          this.listLimitDraft = this.listLimitApplied;
          const list = Array.isArray(res) ? res : [];
          const paid = await paidTablesOutbox.getAllPaidTables();
          const paidSet = new Set(
            (Array.isArray(paid) ? paid : [])
              .map(p => (p?.saleNumber != null ? String(p.saleNumber).trim() : null))
              .filter(Boolean)
          );
          // BUG NOTE (investigation 2026-07-11): This filter hides any table whose saleNumber
          // is present in `paidTablesOutbox` localStorage from the UI's `this.tables`. It does
          // NOT delete the row from `offline_tables` SQLite, nor does it notify the table-server.
          // When the matching `paidTablesOutbox.addPaidTable(...)` in sales-module.js runs
          // without a paired `tableServerClient.removeTable(...)` (because `finalizePaidTable`
          // short-circuited, see sales-module.js line 1931-1971), the table is now "invisible
          // here, alive in the table-server's SQLite". A subsequent `handleCreateTable` POST
          // with the same Name hits the LIKE duplicate-name check at table-server.js:312-332
          // and returns 409. The hide-from-UI here is the most likely "table goes missing"
          // loss point observed by users.
          this.tables = list.filter(t => {
            const sn = (t?.saleNumber || t?.SaleNum);
            // Normalize saleNumber for comparison - trim and handle both string/number
            const snStr = sn != null ? String(sn).trim() : null;
            return !snStr || !paidSet.has(snStr);
          });
          this.$nextTick(this.updateScrollButtons);
          this.totalTip()
        }).finally(() => {
          this._isLoadingTables = false;
          if (!silent) this.$utils.hideLoading()
        })
    },
    async openTable(tab) {
      let store = await Storage.getDefaultStore();
      if (tab.inUse) {
        let isAllowed = await this.$store.dispatch('validateSecurityPermission', 'overrideTable');
        if (isAllowed) {
          const result = await Swal.fire({
            title: 'Warning!',
            text: 'This table is currently in use. Are you sure you want to override and open it?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, override it!',
            cancelButtonText: 'Cancel'
          });
          
          if (!result.isConfirmed) {
            return;
          }
          let supervisor = await Storage.getSupervisor();
          if (!supervisor) {
            let user = await Storage.getCurrentUser();
            supervisor = user.Username;
          }

          try {
            const response = await axios.post(serverApi + `tables/${store.StoreDB}/overrideOpenTable/${tab.saleNumber}?user=${supervisor}`);
            if (response.status === 200) {
              await this.getOpenTables();
              this.setTable = tab;
              this.$refs.tableSheet.open();
            } 
          } catch (error) {
            errorHandler.tomcatError(error);
            console.error(error);
          }
        }
        if (!isAllowed) {
          errorHandler.tomcatError(new Error('You do not have permission to access this table'), true, 'Permission Denied');
          return;
        }
      }
      this.setTable = tab;
      this.$refs.tableSheet.open();
    },
    async totalTip() {
      let saleTotal = this.total;
      let settings = await Storage.getDeviceSettings();
      let tip = 0;
      if (settings && settings.AutoTip > 0) {
        tip = Number(settings.AutoTip);
        tip = (tip / 100) * saleTotal;
      }
      this.tipsTotal = tip;
    },
    /**
     * FAB "New Sale" / "Return to Sale" entry.
     * - Return to Sale (cart has lines): keep currentSale + cart intact.
     * - New Sale (empty cart): clear stale saleNumber BEFORE any API / navigate
     *   so empty creates never POST a previous SaleNum (409 EMPTY_SAVE_REJECTED).
     * WaiterStation create path forces SaleNum: 0 + new reserve via createNewTable.
     * processingNewSale guard lives in beginNewSaleSession / createNewTable.
     */
    async goToPos() {
      this.fabOpen = false;
      // Double-click guard (same pattern as processingGRV)
      if (this.$store.getters.getIsStartingNewSale || this.processingNewSale) {
        console.warn('[AllTables][goToPos] New Sale already in progress — ignoring duplicate click');
        return;
      }
      const hasSaleLines = (this.$store.getters.getSaleLines || []).length > 0;

      // Return to Sale — do not wipe an in-progress cart
      if (hasSaleLines) {
        return this.$router.push({ name: `pos` });
      }

      // Clear stale saleNumber before navigating to New Sale.
      // Without this, the BE guard returns 409 EMPTY_SAVE_REJECTED
      // because the previous saleNumber still has server lines.
      // See Online Sales Server TableService.refuseEmptyWipeIfServerHasItems.
      this.processingNewSale = true;
      try {
        if (this.settings?.PosType === 'WaiterStation') {
          const promptResult = await Swal.fire({
            title: 'Table Name',
            icon: 'question',
            input: 'text',
            inputPlaceholder: 'Enter table name',
            showCancelButton: true,
            confirmButtonText: 'Create Table',
            cancelButtonText: 'Cancel',
            allowOutsideClick: false,
            inputValidator: (value) => {
              if (!value || !value.trim()) return 'Table name is required';
            }
          });

          if (!promptResult.isConfirmed) return;

          const tableName = promptResult.value.trim();
          this.$utils.showLoading();
          try {
            // Same check as Table Server Configuration Connected badge.
            try {
              await tableServerClient.ensureMainPcReachableForTableWrite();
            } catch (connErr) {
              await Swal.fire({
                icon: 'warning',
                title: 'Main Device Unreachable',
                text: connErr?.message || 'Cannot create table while Main device is unreachable.'
              });
              return;
            }

            // createNewTable → beginNewSaleSession + AddOrUpdateTable.
            // Founder 2026-08-09: empty create POSTs with just-allocated saleNum so
            // the BE creates the table header (emptySaveBlocked does NOT apply on create).
            const res = await this.$store.dispatch('createNewTable', {
              name: tableName,
              Customer: this.$store.getters.getCustomer,
              HeadCount: 1,
            });
            // RESTAURANT-POS-Q: concurrent create soft-skipped — stay put (no fake sale#)
            if (res && res.skipped) {
              console.warn('[AllTables] createNewTable soft-skipped (already starting)');
              return;
            }
            // Prefer assigned number only — never fall back to a stale store value
            const saleNumber = res?.SaleNum || res?.saleNumber || 0;
            if (!saleNumber || Number(saleNumber) <= 0) {
              throw new Error('Table was created but no sale number was returned. Please try again.');
            }
            // Close any open dialogs before navigating
            Swal.close();
            Swal.getPopup()?.remove();
            Swal.getContainer()?.remove();
            if (typeof toastr !== 'undefined') {
              toastr.clear();
            }
            // Force hide any modal overlays
            document.querySelectorAll('.modal, .modal-backdrop, .swal2-popup, .swal2-container, .sweet-alert, [role="dialog"]').forEach(el => el.style.display = 'none');
            // Wait for dialogs to close before navigating
            await this.$nextTick();
            await new Promise(r => setTimeout(r, 100));
            // Navigate to POS — table header already created via empty POST create path
            return this.$router.push({ name: `pos`, query: { saleNumber } });
          } catch (error) {
            // EMPTY_SAVE_BLOCKED / silent hydrate — do not open a scary Swal
            if (error?.code === 'EMPTY_SAVE_BLOCKED' || error?.silent === true) {
              console.warn('[AllTables][goToPos] empty create blocked silently', error?.message);
              return;
            }
            const text = String(error?.message || '');
            // Operator-safe copy — never raw "Table Does not Exist" (Victoria brief 2.1.13)
            let friendly = text || 'Main device is unreachable or table creation failed.';
            if (/table\s+does\s+not\s+exist/i.test(text)) {
              friendly = 'This table is no longer available. Refresh and try again.';
            } else if (/EMPTY_SAVE_REJECTED|EMPTY_SAVE_BLOCKED/i.test(text)) {
              friendly = 'This table still has items. Refresh to load them.';
            }
            await Swal.fire({
              icon: 'error',
              title: 'Unable to create table',
              text: friendly
            });
            return; // Don't navigate to POS if table creation failed
          } finally {
            this.$utils.hideLoading();
          }
        }

        // Non-WaiterStation New Sale: wipe state then open a clean POS cart
        await this.$store.dispatch('beginNewSaleSession');
        return this.$router.push({ name: `pos`, query: {} });
      } finally {
        this.processingNewSale = false;
      }
    },
    async mergeTable() {
      if (!this.isOnline) return;
      this.fabOpen = false;
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `mergeTable`);
      if (isAllowed) return this.$router.push({ name: `merge-tables` });
    },
    async userCashup() {
      this.fabOpen = false;
      let openTables = this.tables.length;
      if (openTables > 0) {
        let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `cashupWithOpenTables`);
        if (!isAllowed) return;
      }
      return this.$router.push({ name: `cashup` })
    },
    showPayoutModal() {
      this.fabOpen = false;
      this.$refs.payoutModal.open();
    },
    showDepositModal() {
      this.fabOpen = false;
      this.$refs.depositModal.open();
    },
    async openCashDrawer() {
      this.fabOpen = false;
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `OpenDrwer`);
      if (isAllowed) return this.$store.dispatch(`openCashDrawer`)
    },
  }
}
</script>

<style lang="scss" scoped>
.table_container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--surface-canvas-gradient, var(--surface-canvas));
  position: relative;
}

.table_header {
  background: var(--surface-raised);
  border-bottom: 1px solid var(--border-default);
  box-shadow: var(--card-shadow);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  padding: 1.5rem;
  display: flex;
  gap: 1.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.turnover-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-dark) 100%);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.3);
  flex-shrink: 0;

  .turnover-icon {
    width: 50px;
    height: 50px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
  }

  .turnover-details {
    display: flex;
    flex-direction: column;
    
    .turnover-label {
      color: rgba(255, 255, 255, 0.9);
      font-size: 0.85rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .turnover-amount {
      color: white;
      font-size: 1.8rem;
      font-weight: 700;
      margin: 0;
      line-height: 1.2;
    }
  }
}

.search-filters {
  display: flex;
  gap: 1rem;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-secondary);
    font-size: 1rem;
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 3rem;
    border: 1px solid var(--border-default);
    border-radius: 8px;
    background: var(--surface-sunken);
    color: var(--text-primary);
    font-size: 1rem;
    transition: var(--transition-default);

    &:focus {
      outline: none;
      border-color: var(--accent-primary);
      box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb), 0.15);
    }

    &::placeholder {
      color: var(--text-secondary);
    }
  }
}

.filter-box {
  min-width: 200px;

  :deep(.vs__dropdown-toggle) {
    background: var(--surface-sunken);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    padding: 0.35rem 0.5rem;
  }

  :deep(.vs__selected),
  :deep(.vs__search) {
    color: var(--text-primary);
  }

  :deep(.vs__dropdown-menu) {
    background: var(--surface-raised);
    border: 1px solid var(--border-default);
    box-shadow: var(--card-shadow);
  }
}

.tables-list-limit-box {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 220px;

  .limit-label {
    color: var(--text-secondary);
    font-weight: 600;
    font-size: 0.85rem;
    margin: 0;
  }

  .limit-controls {
    display: flex;
    align-items: stretch;
    gap: 0.35rem;
  }

  .limit-input {
    width: 5.5rem;
    padding: 0.5rem 0.65rem;
    border: 1px solid var(--border-default);
    border-radius: 8px;
    background: var(--surface-sunken);
    color: var(--text-primary);
    font-size: 0.95rem;
    transition: var(--transition-default);

    &:focus {
      outline: none;
      border-color: var(--accent-primary);
      box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb), 0.15);
    }
  }

  .limit-btn {
    min-width: 2.25rem;
    padding: 0.45rem 0.65rem;
    border: 1px solid var(--border-default);
    border-radius: 8px;
    background: var(--surface-muted);
    color: var(--text-primary);
    font-weight: 700;
    cursor: pointer;
    transition: var(--transition-default);

    &:hover {
      background: var(--secondary-button-bg-dark);
      border-color: var(--border-strong);
      color: var(--text-primary);
    }
  }

  .limit-apply-btn {
    padding: 0.45rem 0.9rem;
    border: 1px solid var(--primary-button-bg);
    border-radius: 8px;
    background: var(--primary-button-bg);
    color: var(--primary-button-text);
    font-weight: 600;
    cursor: pointer;
    transition: var(--transition-default);

    &:hover {
      background: var(--primary-button-hover);
      border-color: var(--primary-button-hover);
      color: var(--text-on-accent);
    }
  }

  .limit-hint {
    color: var(--text-muted);
    font-size: 0.8125rem;
    line-height: 1.35;
  }
}

.tables-grid-container {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  position: relative;
}

.table-tabs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  border-bottom: var(--card-border);
}

.table-tabs-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.table-tabs-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.table-tab-btn {
  border: var(--card-border);
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-default);

  &:hover {
    border-color: var(--accent-primary);
  }

  &.active {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }
}

.tables-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  padding-bottom: 100px; // Space for FAB
}

.table-item-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.offline-sync-btn {
  height: 36px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-dark) 100%);
  color: white;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  cursor: pointer;
  transition: var(--transition-default);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(var(--accent-electric-blue-rgb), 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.create-table-btn {
  height: 36px;
  border: var(--card-border);
  border-radius: 8px;
  padding: 0 0.95rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-default);

  &:hover {
    border-color: var(--accent-primary);
    color: var(--accent-primary);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);

  .empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h3 {
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9rem;
  }
}

.scroll-controls {
  position: fixed;
  right: 1.5rem;
  bottom: 8rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 900;
}

.scroll-btn {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: var(--card-bg);
  color: var(--text-primary);
  box-shadow: var(--card-shadow);
  cursor: pointer;
  transition: var(--transition-default);

  &:hover:not(:disabled) {
    background: var(--accent-primary);
    color: white;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

// FAB Styles
.fab-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
}

.fab-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: -1;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fab-actions {
  position: absolute;
  bottom: 80px;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-end;
}

.fab-action-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;

  &.fab-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .fab-label {
    background: var(--card-bg);
    color: var(--text-primary);
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    box-shadow: var(--card-shadow);
    white-space: nowrap;
    opacity: 1; // Always visible
  }

  .fab-action-button {
    width: 56px;
    height: 56px;
    background: var(--card-bg);
    color: var(--accent-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    box-shadow: var(--card-shadow);
    transition: var(--transition-default);

    &:hover {
      background: var(--accent-primary);
      color: white;
      transform: scale(1.1);
    }
  }
}

@keyframes slideInLabel {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.fab-item-enter-active,
.fab-item-leave-active {
  transition: all 0.3s ease;
}

.fab-item-enter-from,
.fab-item-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.8);
}

.fab-main {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-dark) 100%);
  color: white;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(var(--accent-electric-blue-rgb), 0.4);
  transition: var(--transition-default);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(var(--accent-electric-blue-rgb), 0.5);
  }

  &.fab-active {
    transform: rotate(45deg);
  }

  .fab-icon {
    transition: transform 0.3s ease;
  }
}

// Mobile Responsiveness
@media screen and (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem;
  }

  .turnover-card {
    width: 100%;
  }

  .search-filters {
    flex-direction: column;
  }

  .search-box,
  .filter-box {
    min-width: 100%;
  }

  .tables-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    padding-bottom: 100px;
  }

  .table-tabs {
    padding: 0.75rem 1rem;
    flex-wrap: wrap;
  }

  .table-tabs-left {
    width: 100%;
    flex-wrap: wrap;
  }

  .table-tabs-right {
    width: 100%;
    justify-content: flex-end;
  }

  .scroll-controls {
    top: auto;
    bottom: 120px;
  }

  .fab-container {
    bottom: 1rem;
    right: 1rem;
  }

  .fab-main {
    width: 56px;
    height: 56px;
  }

  .fab-action-item {
    .fab-action-button {
      width: 48px;
      height: 48px;
    }

    .fab-label {
      font-size: 0.85rem;
      padding: 0.4rem 0.8rem;
    }
  }
}
</style>
